import { company, type CompanySettings } from '@/stores/company';

/**
 * Every money amount, number, date and time the app shows goes through here,
 * so they all follow Company settings: currency symbol and decimals, lakh or
 * international grouping, date order, 12 or 24 hour clock, and time zone.
 */

/** en-IN groups as 12,34,567 (lakh); en-PK as 1,234,567. */
function numberLocale(c: CompanySettings = company()): string {
  return c.numberGrouping === 'lakh' ? 'en-IN' : 'en-PK';
}

/**
 * The most decimal places the server keeps: quantities and unit costs are
 * stored to 4, money to 2. Anything finer is floating-point noise
 * (0.1 + 0.2 = 0.30000000000000004), never a real figure.
 */
const STORED_PLACES = 4;

/**
 * Rounds half away from zero, as the server does (ROUND_HALF_UP), so a figure
 * worked out on screen matches the one that gets saved. Plain
 * `Math.round(x * 100) / 100` turns 1.005 into 1.00 because 1.005 is really
 * 1.00499999… in binary; the nudge below puts it back on the right side.
 */
export function roundTo(value: number, places: number): number {
  if (!Number.isFinite(value)) return value;
  const f = 10 ** places;
  const r = (Math.sign(value) * Math.round(Math.abs(value) * f * (1 + Number.EPSILON))) / f;
  return r === 0 ? 0 : r; // never -0, which prints as "-0"
}

/** A money amount to the paisa, as the server stores it. */
export function roundMoney(value: number): number {
  return roundTo(value, 2);
}

/**
 * How many decimal places `n` needs so nothing real is hidden: the `min` the
 * setting asks for when the value is exact at that; otherwise the fewest
 * places from `step` up to `max` that show it in full.
 */
function placesNeeded(n: number, min: number, step: number, max: number): number {
  const full = roundTo(n, Math.max(min, max));
  if (roundTo(n, min) === full) return min;
  for (let d = Math.max(min, step); d < max; d++) {
    if (roundTo(n, d) === full) return d;
  }
  return Math.max(min, max);
}

export function formatNumber(value: number | string, decimals?: number, c: CompanySettings = company()): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const places = decimals ?? placesNeeded(n, 0, 0, STORED_PLACES);
  return roundTo(n, places).toLocaleString(numberLocale(c), {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  });
}

/** A count, such as bird capacity or a total of rows. */
export function formatCount(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : formatNumber(value, 0);
}

/**
 * Company currency. A dash when the value is absent, which is what a masked cost is.
 *
 * The company's decimal places are how whole amounts read — "None" shows
 * Rs 1,250, not Rs 1,250.00 — but they never hide paisa that are really
 * there: 2 × Rs 10.20 shows Rs 20.40, not Rs 20. A rounded figure on a
 * receipt is a wrong figure, and the grand total would not add up.
 */
export function formatMoney(value: number | string | null | undefined, c: CompanySettings = company()): string {
  return moneyText(value, c, 2);
}

/**
 * A price for one unit — cost per kg, average cost, rate per day. These are
 * kept to 4 places, so Rs 10.125 per gram reads in full instead of Rs 10.13.
 */
export function formatRate(value: number | string | null | undefined, c: CompanySettings = company()): string {
  return moneyText(value, c, STORED_PLACES);
}

function moneyText(value: number | string | null | undefined, c: CompanySettings, max: number): string {
  if (value === undefined || value === null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  // Paisa come in pairs: Rs 20.40, never Rs 20.4.
  const places = placesNeeded(n, c.currencyDecimals, 2, Math.max(max, c.currencyDecimals));
  return `${c.currencySymbol} ${formatNumber(n, places, c)}`;
}

/** How many decimal places a unit is counted in. */
export const PRECISION_OPTIONS = [
  { value: '0', label: 'Whole numbers (bags, vials)' },
  { value: '1', label: '1 decimal place' },
  { value: '2', label: '2 decimal places (litres, kg)' },
  { value: '3', label: '3 decimal places (grams)' },
];

/**
 * Renders at the unit's own precision, so litres read 1.20 and bags read 12.
 * A fraction the precision would round away still shows — 2.5 bags reads
 * 2.5, not 3 — since stock is kept to 4 places whatever the unit says.
 */
export function formatQuantity(value: number | string, precision = 2): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return formatNumber(n, placesNeeded(n, precision, precision, Math.max(precision, STORED_PLACES)));
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Year, month and day of `d` in the company's time zone. */
function partsInZone(d: Date, timeZone: string): { y: number; m: number; d: number; h: number; min: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(d);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    y: get('year'),
    m: get('month'),
    d: get('day'),
    h: get('hour') % 24,
    min: get('minute'),
  };
}

function assembleDate(y: number, m: number, d: number, format: CompanySettings['dateFormat']): string {
  const dd = String(d).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  switch (format) {
    case 'MM/DD/YYYY':
      return `${mm}/${dd}/${y}`;
    case 'YYYY-MM-DD':
      return `${y}-${mm}-${dd}`;
    case 'DD-MMM-YYYY':
      return `${dd}-${MONTHS[m - 1]}-${y}`;
    default:
      return `${dd}/${mm}/${y}`;
  }
}

function toDate(value: string | Date): Date | null {
  const d = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A calendar date. "2026-09-29" is taken as that day everywhere, not as UTC midnight. */
export function formatDate(value: string | Date | null | undefined, c: CompanySettings = company()): string {
  if (!value) return '—';
  if (typeof value === 'string') {
    const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (plain) return assembleDate(Number(plain[1]), Number(plain[2]), Number(plain[3]), c.dateFormat);
  }
  const d = toDate(value);
  if (!d) return '—';
  const p = partsInZone(d, c.timezone);
  return assembleDate(p.y, p.m, p.d, c.dateFormat);
}

export function formatTime(value: string | Date | null | undefined, c: CompanySettings = company()): string {
  if (!value) return '—';
  const d = toDate(value);
  if (!d) return '—';
  const p = partsInZone(d, c.timezone);
  const mm = String(p.min).padStart(2, '0');
  if (c.timeFormat === '24h') return `${String(p.h).padStart(2, '0')}:${mm}`;
  const h12 = p.h % 12 === 0 ? 12 : p.h % 12;
  return `${h12}:${mm} ${p.h < 12 ? 'am' : 'pm'}`;
}

/** A moment as "5:22 am" when it is today, and with its date on any other day. */
export function formatSince(value: Date | number, now: Date = new Date()): string {
  const when = new Date(value);
  return formatDate(when) === formatDate(now) ? formatTime(when) : formatDateTime(when);
}

/** Date and time to the minute, as in the audit log. */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return `${formatDate(value)}, ${formatTime(value)}`;
}

/**
 * The calendar day a moment falls on in the company's time zone, as a count
 * of days. Two moments are "the same day" or "a day apart" by subtracting
 * these, so 11 pm and 1 am are a day apart in Karachi whatever the browser's
 * own clock is set to.
 */
export function companyDay(value: Date, c: CompanySettings = company()): number {
  const p = partsInZone(value, c.timezone);
  return Date.UTC(p.y, p.m - 1, p.d) / 86_400_000;
}

/** The company's time zone, for the header clock and calendar. */
export function companyTimeZone(): string {
  return company().timezone;
}
